import React, { useState, useEffect } from 'react';
import BaseTable from '../Components/BaseTable';
import styles from '../Styles/ViewOfUsersPages.module.css';
import { useAuth } from '../Components/AuthContext';

// Define each account type and its API path segment
const requestTypes = [
    { label: 'Healthcare Worker', path: 'healthcare-worker' },
    { label: 'GMF Employee', path: 'gmf-employee' },
    { label: 'Institution', path: 'institution-account' },
];

const ApproveRequests = () => {
    const [requests, setRequests] = useState([]);
    const [error, setError] = useState(null);
    const url = process.env.REACT_APP_API_URL;
    const { token } = useAuth();

    // Fetch unverified requests for all account types
    const fetchRequests = async () => {
        try {
            const allData = await Promise.all(
                requestTypes.map(async ({ label, path }) => {
                    const res = await fetch(
                        `${url}/${path}/list?verified=false`,
                        {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );

                    if (!res.ok)
                        throw new Error(
                            `Failed to fetch ${label} unverified accounts`
                        );
                    const data = await res.json();

                    // Normalize each item: pick the correct id field
                    return data.map((item) => {
                        return {
                            ...item,
                            accountId: item.id,
                            userId: item.user_id,
                            id: `${path}-${item.id}`, // DataGrid’s unique row key
                            accountType: label,
                            endpoint: path,
                            name: item.institution_name
                                ? item.institution_name
                                : `${item.first_name} ${item.last_name}`,
                            status: item.status || 'pending',
                        };
                    });
                })
            );
            setRequests(allData.flat());
        } catch (err) {
            console.error(err);
            setError('Error loading account requests.');
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    // Approve or deny via PUT with { status: 'approve'|'deny' }
    const handleAction = async (id, endpoint, action) => {
        try {
            const res = await fetch(
                `${url}/${endpoint}/verify/${id}?status=${action}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            if (!res.ok) throw new Error(`Failed to ${action} request`);

            setRequests((prev) =>
                prev.map((req) =>
                    req.id === id && req.endpoint === endpoint
                        ? {
                              ...req,
                              status:
                                  action === 'approve' ? 'approved' : 'denied',
                          }
                        : req
                )
            );
            await fetchRequests(); // Refresh the list
        } catch (err) {
            console.error(err);
            setError(`Error performing ${action} on request`);
        }
    };

    const columns = [
        {
            field: 'name',
            headerName: 'Name',
            width: 300,
            flex: 1,
        },
        { field: 'email', headerName: 'Email', width: 200, flex: 1 },
        {
            field: 'accountType',
            headerName: 'Account Type',
            width: 180,
            flex: 1,
            // don't change anything in the data but display GMF Employee as Global Medic Force
            // otherwise just display 'Institution' and "Healthcare Worker" normally
            renderCell: (params) => (
                // if the value is GMF Employee, display Global Medic Force, else just display the value
                <span>
                    {params.value === 'GMF Employee'
                        ? 'Global Medic Force'
                        : params.value}
                </span>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 130,
            renderCell: (params) => (
                <span className={`status-${params.value}`}>
                    {params.value.charAt(0).toUpperCase() +
                        params.value.slice(1)}
                </span>
            ),
            flex: 1,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 300,
            sortable: false,
            filterable: false,
            renderCell: (params) => actionButtons(params.row),
        },
    ];

    const actionButtons = (row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
            {row.status === 'pending' && (
                <>
                    {/* Approve button: styled as green and tracks if it was clicked */}
                    <button
                        // don't do "styles.greenButton" because styles is not a CSS module
                        className="greenButton"
                        onClick={() =>
                            handleAction(row.accountId, row.endpoint, 'approve')
                        }
                    >
                        Approve
                    </button>
                    {/* Deny button: styled as red and tracks if it was clicked */}
                    <button
                        // don't do "styles.greenButton" because styles is not a CSS module
                        className="redButton"
                        onClick={() =>
                            handleAction(row.accountId, row.endpoint, 'deny')
                        }
                    >
                        Deny
                    </button>
                </>
            )}
        </div>
    );

    return (
        <div className={styles.viewOfUsersContainer}>
            <h2>Account Requests</h2>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.tableWrapper}>
                <BaseTable rows={requests} columns={columns} />
            </div>
        </div>
    );
};

export default ApproveRequests;
